export type ApplicantSubscriptionPlanCode = "STANDARD" | "PREMIUM";
export type ApplicantSubscriptionStatus = "REQUESTED" | "ACTIVE" | "CANCELED";

export type ApplicantSubscriptionPlan = {
  code: ApplicantSubscriptionPlanCode;
  name: string;
  monthlyPriceUsd: number;
  description: string;
  highlights: string[];
  recommended?: boolean;
};

export type ApplicantSubscriptionRecord = {
  id: string;
  productKey: string;
  subscriptionKind: "APPLICANT";
  billingProvider: "STRIPE";
  planCode: ApplicantSubscriptionPlanCode;
  planName: string;
  priceMonthlyUsd: number;
  currency: string;
  status: ApplicantSubscriptionStatus;
  source: string;
  fullName: string;
  email: string;
  phone?: string | null;
  userId?: number | null;
  notes?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  requestedAt: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateApplicantSubscriptionInput = {
  planCode: ApplicantSubscriptionPlanCode;
  fullName: string;
  email: string;
  phone?: string;
  userId?: number | null;
  notes?: string;
};

export const APPLICANT_SUBSCRIPTION_PLANS: ApplicantSubscriptionPlan[] = [
  {
    code: "STANDARD",
    name: "Standard",
    monthlyPriceUsd: 9,
    description:
      "A simple membership for caregivers who want stronger job follow-up while keeping free search and apply.",
    highlights: [
      "Everything in Free stays available",
      "Priority job alert signup",
      "Standard follow-up from Plany365 when matching roles open",
      "A saved membership account for your care jobs plan",
    ],
    recommended: true,
  },
  {
    code: "PREMIUM",
    name: "Premium",
    monthlyPriceUsd: 19,
    description:
      "Best for caregivers who want stronger visibility, faster follow-up, and added support during their job search.",
    highlights: [
      "Everything in Standard",
      "Priority follow-up from Plany365",
      "Extra support for urgent or hard-to-match opportunities",
      "A higher-touch care jobs membership account",
    ],
  },
];

export const FREE_APPLICANT_PLAN = {
  name: "Free",
  monthlyPriceUsd: 0,
  description:
    "Search jobs, view details, apply, save jobs, keep a caregiver profile, and sign up for free alerts.",
  highlights: [
    "Search caregiving jobs for free",
    "Apply to open jobs for free",
    "Save jobs and keep a profile",
    "Get free care job alerts",
  ],
};

function buildApiBase() {
  if (typeof window === "undefined") return "/api/applicant-subscriptions";

  const { protocol, hostname, port, origin } = window.location;
  if (
    (hostname === "localhost" || hostname === "127.0.0.1") &&
    (port === "3000" || port === "3001")
  ) {
    return `${protocol}//${hostname}:8080/api/applicant-subscriptions`;
  }

  return `${origin}/api/applicant-subscriptions`;
}

function buildPlansApiUrl() {
  return `${buildApiBase()}/plans`;
}

async function parseJsonSafe(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export function getApplicantSubscriptionPlan(planCode?: string | null) {
  const normalized = String(planCode || "").trim().toUpperCase();
  return (
    APPLICANT_SUBSCRIPTION_PLANS.find((plan) => plan.code === normalized) || null
  );
}

export function getApplicantSubscriptionStatusLabel(status?: string | null) {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "ACTIVE") return "Active";
  if (normalized === "CANCELED") return "Canceled";
  return "Requested";
}

export async function listApplicantSubscriptionPlans() {
  const response = await fetch(buildPlansApiUrl(), {
    method: "GET",
    headers: { accept: "application/json" },
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || "Failed to load applicant subscription plans.");
  }

  const items = Array.isArray(payload.items) ? payload.items : [];
  return (items.length ? items : APPLICANT_SUBSCRIPTION_PLANS) as ApplicantSubscriptionPlan[];
}

export async function listApplicantSubscriptions(filters: {
  userId?: number | null;
  email?: string | null;
}) {
  const params = new URLSearchParams();
  if (filters.userId != null) params.set("userId", String(filters.userId));
  if (filters.email) params.set("email", String(filters.email).trim());
  const url = `${buildApiBase()}${params.toString() ? `?${params.toString()}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || "Failed to load applicant subscriptions.");
  }

  return (Array.isArray(payload.items) ? payload.items : []) as ApplicantSubscriptionRecord[];
}

export async function getLatestApplicantSubscription(filters: {
  userId?: number | null;
  email?: string | null;
}) {
  const items = await listApplicantSubscriptions(filters);
  return items[0] || null;
}

export async function createApplicantSubscription(input: CreateApplicantSubscriptionInput) {
  const response = await fetch(buildApiBase(), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(input),
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || "Failed to save applicant subscription.");
  }

  return payload as {
    ok: true;
    created: boolean;
    subscription: ApplicantSubscriptionRecord;
  };
}

export async function cancelApplicantSubscription(id: string) {
  const response = await fetch(`${buildApiBase()}/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || "Failed to cancel applicant subscription.");
  }

  return payload as {
    ok: true;
    subscription: ApplicantSubscriptionRecord;
  };
}
